import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatTableModule} from '@angular/material/table';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {User} from '../api/interfaces/user.interface';
import {MatAutocompleteModule, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {catchError, debounceTime, map, Observable, of, startWith, switchMap} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {MapService} from '../api/services/map.service';
import {MatTooltipModule} from '@angular/material/tooltip';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {isEmpty, set} from 'lodash';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {UserInitialsBadgeComponent} from '../shared/components/user-menu/user-badge.component';

@Component({
  selector: 'maporium-transfer-map-dialog',
  styles: [`
    ::ng-deep {

      .share-map-form {
        --mdc-theme-primary: #000000;

        .mdc-floating-label {
          color: #000000 !important;
        }

        .mat-mdc-text-field-wrapper {
          height: auto;
        }

        .mat-mdc-form-field-infix {
          min-height: unset;
          padding-top: 6px !important;
          padding-bottom: 6px !important;
        }
      }

    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0"><span class="material-symbols-rounded me-2">passkey</span>
        {{ 'MISC_DIALOGS.TRANSFER.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <div mat-dialog-content class="p-3 px-4">
      <form class="share-map-form mb-3">
        <mat-form-field class="w-100" appearance="outline" subscriptSizing="dynamic">
          <input #userInput
                 matInput
                 [formControl]="userCtrl"
                 [matAutocomplete]="auto"
                 [placeholder]="'SHARE_DIALOG.SEARCH_PLACEHOLDER' | translate"
                 [attr.aria-label]="'MISC_DIALOGS.TRANSFER.USER_SELECTION_ARIA' | translate"
                 (keydown.enter)="$event.preventDefault()">
          <mat-autocomplete #auto="matAutocomplete"
                                class="compact-autocomplete-panel"
                            [displayWith]="displayUser"
                            (optionSelected)="selected($event)">
            <mat-option *ngFor="let user of filteredUsers | async" [value]="user">
              <app-user-initials-badge class="me-2"
                                       [user]="user"
                                       [size]="28"
                                       [tooltip]="getUserTooltip(user)"
                                       tooltipPosition="right"></app-user-initials-badge>
              {{ getUserDisplay(user) }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </form>

      <div *ngIf="selectedUser" class="my-2">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <app-user-initials-badge class="me-2"
                                     [user]="selectedUser"
                                     [size]="28"
                                     [tooltip]="getUserTooltip(selectedUser)"
                                     tooltipPosition="right"></app-user-initials-badge>
            {{ getUserName(selectedUser) }} &lt;{{ selectedUser.email }}&gt;
          </div>
          <button mat-icon-button (click)="clearSelection()"
                  [matTooltip]="'MISC_DIALOGS.TRANSFER.CLEAR_SELECTION_TOOLTIP' | translate">
            <mat-icon fontSet="material-symbols-rounded">delete</mat-icon>
          </button>
        </div>
      </div>
    </div>
    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close>{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="update()" [disabled]="!selectedUser">{{ 'MISC_DIALOGS.TRANSFER.TRANSFER_BUTTON' | translate }}</button>
    </div>
  `,
  standalone: true,
  imports: [
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatTableModule,
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    UserInitialsBadgeComponent,
    TranslatePipe
  ]
})
export class TransferMapDialogComponent {
  userCtrl = new FormControl('');
  filteredUsers: Observable<User[]>;
  selectedUser: User | null = null;

  //@ts-ignore
  @ViewChild('userInput') userInput: ElementRef<HTMLInputElement>;

  constructor(private userService: UserService,
              private mapService: MapService,
              private dialog: MatDialog,
              private dialogRef: MatDialogRef<TransferMapDialogComponent>,
              private translate: TranslateService,
              @Inject(MAT_DIALOG_DATA) private data: { map: GraphMap }) {
    // Same privacy-gated, server-side search the share dialog uses: the
    // backend only returns the current user's share connections matching the
    // typed name/email, plus an exact full-email match.
    this.filteredUsers = this.userCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(250),
      map(value => (typeof value === 'string' ? value : '')),
      switchMap(term =>
        this.userService.getAvailableUsersForTransfer(term.trim()).pipe(
          map(users => users.filter(u => u.email !== this.selectedUser?.email)),
          catchError(() => of([] as User[]))
        )
      )
    );
  }

  getUserDisplay(user: User): string {
    const name = this.getUserName(user);
    return name ? `${name} <${user.email}>` : (user.email || '');
  }

  getUserName(user: User): string {
    return `${user.name || ''}${!isEmpty(user.lastName) ? ' ' + user.lastName : ''}`.trim();
  }

  getUserTooltip(user?: User | null): string {
    if (!user) return '';
    const fullName = `${user.name || ''} ${user.lastName || ''}`.trim();
    const org = user.organization ? ` (${user.organization})` : '';
    const bio = user.biography ? `: ${user.biography}` : '';
    return `${fullName}${org}${bio}`.trim();
  }

  displayUser = (user: User | string | null): string => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    return this.getUserDisplay(user);
  };

  selected(event: MatAutocompleteSelectedEvent): void {
    const user: User = event.option.value;
    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);

    if (!user?.email) return;

    // A transfer has exactly one recipient, so picking someone replaces
    // whoever was chosen before rather than adding to a list.
    this.selectedUser = user;
  }

  clearSelection(): void {
    this.selectedUser = null;
    this.userCtrl.setValue('');
  }

  update() {
    const newOwner = this.selectedUser;
    if (!newOwner) return;
    const confirmation = this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('MISC_DIALOGS.TRANSFER.CONFIRM_TITLE'),
        icon: 'warning',
        message: this.translate.instant('MISC_DIALOGS.TRANSFER.CONFIRM_MESSAGE', {name: this.data.map.name, email: newOwner.email})
      }
    });
    confirmation.afterClosed().subscribe((result) => {
      if (result) {
        this.mapService.changeOwner(this.data.map.id, newOwner.id as string)
          .subscribe((res) => {
            set(res, 'isReadonly', true);
            this.mapService.setCurrentSelectedMapToStore(res as any);
            setTimeout(() => {
              location.reload();
            }, 100);
          });
      }
    });
  }
}
